import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in name, email, and message");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Thanks — we'll get back to you soon.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-background" data-testid="contact-page">
            <section className="max-w-6xl mx-auto px-6 pt-16 pb-10">
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">
                    Contact Us
                </div>
                <h1 className="font-display text-4xl sm:text-5xl text-primary leading-[1.05]">
                    Let's talk about your
                    <br />
                    learning goals
                </h1>
                <p className="mt-6 text-lg text-foreground/75 max-w-2xl leading-relaxed">
                    Whether you're curious about a program, need help with enrollment, or want to
                    explore partnership opportunities — our team is here to help.
                </p>
            </section>

            <section className="max-w-6xl mx-auto px-6 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
                    {/* Form */}
                    <Card className="border-border">
                        <CardContent className="p-8">
                            <h2 className="font-display text-2xl text-primary mb-1">
                                Send us a message
                            </h2>
                            <p className="text-sm text-muted-foreground mb-6">
                                We typically respond within one business day.
                            </p>
                            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>Full name</Label>
                                        <Input
                                            value={form.name}
                                            onChange={(e) =>
                                                setForm({ ...form, name: e.target.value })
                                            }
                                            placeholder="Your name"
                                            data-testid="contact-name"
                                        />
                                    </div>
                                    <div>
                                        <Label>Email</Label>
                                        <Input
                                            type="email"
                                            value={form.email}
                                            onChange={(e) =>
                                                setForm({ ...form, email: e.target.value })
                                            }
                                            placeholder="you@example.com"
                                            data-testid="contact-email"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>Phone (optional)</Label>
                                        <Input
                                            value={form.phone}
                                            onChange={(e) =>
                                                setForm({ ...form, phone: e.target.value })
                                            }
                                            placeholder="+1 (___) ___ ____"
                                            data-testid="contact-phone"
                                        />
                                    </div>
                                    <div>
                                        <Label>Subject</Label>
                                        <Input
                                            value={form.subject}
                                            onChange={(e) =>
                                                setForm({ ...form, subject: e.target.value })
                                            }
                                            placeholder="How can we help?"
                                            data-testid="contact-subject"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Message</Label>
                                    <Textarea
                                        rows={6}
                                        value={form.message}
                                        onChange={(e) =>
                                            setForm({ ...form, message: e.target.value })
                                        }
                                        placeholder="Tell us a bit about what you're looking for…"
                                        data-testid="contact-message"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    size="lg"
                                    className="bg-primary hover:bg-primary/90"
                                    data-testid="contact-submit"
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    {submitting ? "Sending…" : "Send message"}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>

                    {/* Contact sidebar */}
                    <div className="space-y-4">
                        <Card className="border-border">
                            <CardContent className="p-6 space-y-5">
                                <div>
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                                            <Phone className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                                Phone
                                            </div>
                                            <a
                                                href="tel:+16045935400"
                                                className="text-foreground hover:text-primary font-medium"
                                            >
                                                +1 (604) 593-5400
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                                            <Mail className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                                Email
                                            </div>
                                            <a
                                                href="mailto:contact@cambridgecollegeonline.com"
                                                className="text-foreground hover:text-primary font-medium"
                                            >
                                                contact@cambridgecollegeonline.com
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                                            <MapPin className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                                Campus
                                            </div>
                                            <div className="text-foreground font-medium">
                                                Surrey, BC
                                            </div>
                                            <div className="text-sm text-muted-foreground mt-0.5">
                                                Victoria, BC — coming soon
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-start gap-3">
                                        <div className="h-9 w-9 flex-shrink-0 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                                            <Clock className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                                Hours
                                            </div>
                                            <div className="text-foreground font-medium">
                                                Mon – Fri, 9:00 – 17:00 PT
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-border bg-primary text-primary-foreground">
                            <CardContent className="p-6">
                                <div className="font-display text-lg mb-1 text-primary-foreground">
                                    Current student?
                                </div>
                                <p className="text-sm text-primary-foreground/80 mb-4">
                                    Log in to continue learning, track your progress, and download
                                    certificates.
                                </p>
                                <Button
                                    asChild
                                    variant="outline"
                                    className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                                >
                                    <a href="/login?as=student">Student login</a>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
}
